import styled from '@emotion/styled';

import FileContainer from './FileContainer';
import ProgressLoader from './ProgressLoader';

import { BlobFile, Size } from '@/library/types/files';
import { FC, ReactNode, useRef, useState } from 'react';

import add from '@/library/assets/svg/actions/add.svg';
import SvgRender from '@/library/components/general/SvgRender';
import useTooltipID from '@/library/utils/hooks/useTooltipID';
import Tooltip from '@/library/components/general/Tooltip';

type UploadedButtonTypes = {
  size: Size;
  onUpload: (acceptedFiles: BlobFile[]) => void;
  isLoading: boolean;
  uploadError?: string | null;
  uploadText?: ReactNode;
  singleUpload?: boolean;
  color?: string;
  accept?: string;
};

const spacing: {
  [key in 'lg' | 'sm' | 'xs']: string;
} = { lg: '26%', sm: '10%', xs: '10%' };

const UploadedButton: FC<UploadedButtonTypes> = ({
  size,
  onUpload,
  isLoading,
  uploadError,
  uploadText,
  singleUpload,
  color,
  accept
}) => {
  const { tooltipID, avoidRender } = useTooltipID();
  const buttonSize = size === 'full' ? 'sm' : size;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Handle file selection from input
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onUpload(Array.from(files) as BlobFile[]);
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    }
  };

  // Handle click to open file dialog
  const handleClick = () => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  // Drag event handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if we're actually leaving the drop zone
    if (e.currentTarget === e.target) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onUpload(Array.from(files) as BlobFile[]);
    }
  };

  return (
    <div
      className={`d-flex flex-column ${buttonSize === 'lg' && !singleUpload ? 'mb-1' : ''} text-${
        uploadError ? 'red' : color
      }`}
      data-testid="files-upload-button"
    >
      <div className="d-flex align-items-center">
        <FileContainer size={buttonSize} id={tooltipID}>
          <Button
            active={isDragActive}
            className="d-flex align-items-center justify-content-center"
            style={{ padding: spacing[buttonSize] }}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              disabled={isLoading}
              accept={accept}
              multiple={!singleUpload}
              style={{ display: 'none' }}
            />
            <Icon className="d-flex align-items-center justify-content-center">
              <SvgRender
                className="justify-content-center"
                src={add}
                style={{ width: '100%', height: '100%' }}
              />
            </Icon>
            {isLoading && <ProgressLoader size={buttonSize} />}
          </Button>
        </FileContainer>
        {(buttonSize === 'lg' || uploadText) && (
          <div className="ms-1 text-primary fs-12 fw-medium">
            {uploadText || `Upload file${!singleUpload ? '(s)' : ''}`}
          </div>
        )}
      </div>

      {uploadError && buttonSize === 'lg' && (
        <FileContainer size={buttonSize} height="initial">
          <div className="text-truncate text-nowrap fs-10 fw-normal cmt-2 cursor-pointer">
            {uploadError}
          </div>
        </FileContainer>
      )}

      {!avoidRender && uploadError ? (
        <Tooltip fade={false} target={tooltipID} innerClassName="fs-12">
          {uploadError}
        </Tooltip>
      ) : null}
    </div>
  );
};

const Button = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  border-width: 1px;
  border-color: currentColor;
  border-style: ${({ active }: { active?: boolean }) => (active ? 'solid' : 'dashed')};
  border-radius: inherit;
  background-color: ${({ active }: { active?: boolean }) =>
    active ? 'rgba(53,64,105,0.1)' : 'rgba(53, 64, 105, 0.05)'};
  cursor: pointer;

  &:hover {
    border-style: solid;
  }
`;

const Icon = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 100%;
  background: white;
  padding: 25%;
`;

export default UploadedButton;
